/**
 * PropertiesPanel - Flow component property editor
 *
 * This component uses the FlowComponentRegistry to dynamically render property editors
 * based on the selected component. No switch statements or hardcoded logic.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Alert,
  Chip,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { flowComponentRegistry } from './flow-components';
import ScreenPanel from './ScreenPanel';
import ConditionBuilder from './ConditionBuilder';

// Component types excluded from the visibility condition section
// If/Switch manage their own condition internally; Footer/NavigationList use a simple visible toggle
const NO_CONDITION_TYPES = new Set(['Footer', 'NavigationList', 'If', 'Switch']);

const PropertiesPanel = ({
  selectedComponent,
  onUpdateComponent,
  currentScreen,
  screens,
  flowType = 'STATIC',
  onRenameScreen,
  onUpdateRouting,
}) => {
  const [properties, setProperties] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);

  useEffect(() => {
    if (selectedComponent) {
      setProperties(selectedComponent.properties || {});

      const flowComponent = flowComponentRegistry.get(selectedComponent.type);
      if (flowComponent) {
        const validation = flowComponent.validateProperties(selectedComponent.properties || {}, { screens });
        setValidationErrors(validation.errors || []);
        setValidationWarnings(validation.warnings || []);
      }
    } else {
      setProperties({});
      setValidationErrors([]);
      setValidationWarnings([]);
    }
  }, [selectedComponent, screens]);

  if (!selectedComponent) {
    return (
      <ScreenPanel
        currentScreen={currentScreen}
        screens={screens}
        onRenameScreen={onRenameScreen}
        onUpdateRouting={onUpdateRouting}
      />
    );
  }

  const flowComponent = flowComponentRegistry.get(selectedComponent.type);

  if (!flowComponent) {
    return (
      <Box className="properties-panel" data-testid="properties-panel">
        <Typography variant="h6" gutterBottom>
          Properties
        </Typography>
        <Alert severity="error">
          Unknown component type: {selectedComponent.type}
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          This component type is not registered in the plugin system.
        </Typography>
      </Box>
    );
  }

  const handlePropertyChange = (propertyName, value) => {
    const oldValue = properties[propertyName];
    const newProperties = { ...properties, [propertyName]: value };

    setProperties(newProperties);
    onUpdateComponent(selectedComponent.id, newProperties);

    if (flowComponent.onPropertyChange) {
      flowComponent.onPropertyChange(propertyName, oldValue, value, { selectedComponent });
    }

    const validation = flowComponent.validateProperties(newProperties, { screens });
    setValidationErrors(validation.errors || []);
    setValidationWarnings(validation.warnings || []);
  };

  const showVisibilitySection = !NO_CONDITION_TYPES.has(selectedComponent.type);
  const hasCondition = !!properties._condition;
  const showAdvancedSettings =
    NO_CONDITION_TYPES.has(selectedComponent.type) || !!flowComponent.maxPerScreen;

  return (
    <Box className="properties-panel" data-testid="properties-panel">
      <Typography variant="h6" gutterBottom>
        Properties
      </Typography>

      {/* Component Info */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="primary">
          {flowComponent.icon} {flowComponent.name}
        </Typography>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Validation Errors:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validationErrors.map((error, index) => (
              <li key={index}>
                <Typography variant="caption">{error}</Typography>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Validation Warnings */}
      {validationWarnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Warnings:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validationWarnings.map((warning, index) => (
              <li key={index}>
                <Typography variant="caption">{warning}</Typography>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Flow component property editor */}
      <Box sx={{ mb: 2 }}>
        {flowComponent.renderPropertyEditor(properties, handlePropertyChange, {
          selectedComponent,
          screens,
          currentScreenId: currentScreen?.id,
          flowType,
          isDynamicFlow: flowType === 'DYNAMIC',
        })}
      </Box>

      {/* Visibility Condition section, injected for all non-navigation components */}
      {showVisibilitySection && (
        <>
          <Divider sx={{ my: 2 }} />
          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VisibilityIcon
                  fontSize="small"
                  sx={{ color: hasCondition ? 'primary.main' : 'text.disabled' }}
                />
                <Typography variant="body2">Visibility Condition</Typography>
                {hasCondition && (
                  <Chip label="Active" size="small" color="primary" sx={{ height: 16, fontSize: '0.65rem' }} />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <FormControlLabel
                control={
                  <Switch
                    checked={hasCondition}
                    onChange={e => {
                      handlePropertyChange(
                        '_condition',
                        e.target.checked
                          ? { mode: 'simple', field: '', operator: 'eq', value: '', expression: '' }
                          : null
                      );
                    }}
                  />
                }
                label="Only show when condition is met"
                sx={{ mb: 1, display: 'flex' }}
              />
              {hasCondition && (
                <ConditionBuilder
                  condition={properties._condition}
                  onChange={cond => handlePropertyChange('_condition', cond)}
                  screenComponents={
                    currentScreen?.components.filter(c => c.id !== selectedComponent.id) || []
                  }
                  label="Show when"
                  helperText="This component shows only when the rule is true."
                  screens={screens}
                  currentScreenId={currentScreen?.id}
                />
              )}
            </AccordionDetails>
          </Accordion>
        </>
      )}

      {showAdvancedSettings && (
        <>
          <Divider sx={{ my: 2 }} />

          {/* Advanced Settings */}
          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2">Advanced Settings</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Footer keeps its own visible toggle; all others use the condition section above */}
                {NO_CONDITION_TYPES.has(selectedComponent.type) && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={properties.visible !== false}
                        onChange={e => handlePropertyChange('visible', e.target.checked)}
                      />
                    }
                    label="Visible"
                  />
                )}

                {flowComponent.maxPerScreen && (
                  <Typography variant="caption" color="text.secondary">
                    Maximum {flowComponent.maxPerScreen} {flowComponent.name} component(s) allowed per screen
                  </Typography>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Box>
  );
};

export default PropertiesPanel;
